import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FiCalendar, FiClock, FiUser, FiTag, FiArrowLeft, FiArrowRight, FiShare2, FiBookmark, FiHeart, FiMessageCircle, FiEye } from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { apiGet } from '../services/apiService';
import RichTextRenderer from '../components/RichTextRenderer';

const BlogDetail = () => {
  const { slug } = useParams();
  const [isBookmarked, setIsBookmarked] = useState(false);
  const [isLiked, setIsLiked] = useState(false);
  const [post, setPost] = useState(null);
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadBlogs = async () => {
      try {
        const data = await apiGet('blog');
        let blogsData;
        if (data.posts) {
          blogsData = data.posts.map(blog => ({
            id: blog.id,
            title: blog.title,
            content: blog.content,
            excerpt: blog.excerpt,
            image: blog.featured_image,
            slug: blog.slug,
            created_at: blog.created_at,
            updated_at: blog.updated_at,
            author: blog.author_name,
            category: blog.category_name,
            tags: JSON.parse(blog.tags || '[]'),
            status: blog.is_published ? 'published' : 'draft',
            featured: blog.is_featured === 1,
            publishedAt: blog.published_at
          }));
        } else {
          blogsData = data.map(blog => ({
            id: blog.id,
            title: blog.title,
            content: blog.content,
            excerpt: blog.excerpt,
            image: blog.featured_image,
            slug: blog.slug,
            created_at: blog.created_at,
            updated_at: blog.updated_at,
            author: blog.author_name,
            category: blog.category_name,
            tags: JSON.parse(blog.tags || '[]'),
            status: blog.is_published ? 'published' : 'draft',
            featured: blog.is_featured === 1,
            publishedAt: blog.published_at
          }));
        }
        setBlogs(blogsData);
        const foundPost = blogsData.find(p => p.slug === slug);
        setPost(foundPost);
      } catch (error) {
        console.error('Failed to load blogs:', error);
        setBlogs([]);
        setPost(null);
      } finally {
        setLoading(false);
      }
    };

    loadBlogs();
  }, [slug]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: post?.title,
        text: post?.excerpt,
        url: window.location.href,
      });
    } else {
      navigator.clipboard.writeText(window.location.href);
      // You could show a toast notification here
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 via-white to-emerald-50/30">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading article...</p>
        </div>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 via-white to-emerald-50/30">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.6 }}
          className="text-center bg-white rounded-3xl p-12 shadow-2xl border border-gray-100/50 max-w-md mx-4"
        >
          <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <SafeIcon icon={FiTag} className="w-10 h-10 text-gray-400" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-4">Article Not Found</h1>
          <p className="text-gray-600 mb-8 leading-relaxed">
            The article you're looking for doesn't exist or may have been moved.
          </p>
          <Link
            to="/blog"
            className="inline-flex items-center gap-3 bg-emerald-500 hover:bg-emerald-600 text-white px-8 py-4 rounded-full font-bold transition-all duration-300 shadow-lg hover:shadow-xl"
          >
            <SafeIcon icon={FiArrowLeft} />
            <span>Back to Blog</span>
          </Link>
        </motion.div>
      </div>
    );
  }

  // Related posts (same category, excluding current)
  const relatedPosts = blogs
    .filter(p => p.category === post.category && p.id !== post.id)
    .slice(0, 3);

  return (
    <div className="min-h-screen bg-white">
      {/* Modern Header */}
      <header className="border-b border-gray-100 bg-white/80 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 lg:h-20">
            {/* Back Navigation */}
            <Link
              to="/blog"
              className="group flex items-center gap-3 text-gray-600 hover:text-emerald-600 transition-colors duration-200"
            >
              <div className="p-2 rounded-full bg-gray-100 group-hover:bg-emerald-100 transition-colors duration-200">
                <SafeIcon icon={FiArrowLeft} className="w-4 h-4" />
              </div>
              <span className="font-medium hidden sm:block">Back to Blog</span>
            </Link>

            {/* Share Button */}
            <button
              onClick={handleShare}
              className="flex items-center gap-2 px-4 py-2 text-gray-600 hover:text-emerald-600 transition-colors duration-200"
            >
              <SafeIcon icon={FiShare2} className="w-4 h-4" />
              <span className="font-medium hidden sm:block">Share</span>
            </button>
          </div>
        </div>
      </header>

      {/* Article Header */}
      <section className="pt-8 pb-12 lg:pt-16 lg:pb-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            {/* Category Badge */}
            <div className="mb-6">
              <span className="inline-flex items-center gap-2 px-3 py-1.5 bg-emerald-50 text-emerald-700 text-sm font-semibold rounded-full border border-emerald-200">
                <SafeIcon icon={FiTag} className="w-3.5 h-3.5" />
                <span>{post.category}</span>
              </span>
            </div>

            {/* Title */}
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-8 leading-tight">
              {post.title}
            </h1>

            {/* Article Meta */}
            <div className="flex flex-wrap items-center gap-6 text-sm text-gray-600 mb-8 pb-8 border-b border-gray-100">
              <div className="flex items-center gap-2">
                <SafeIcon icon={FiCalendar} className="w-4 h-4 text-emerald-500" />
                <time className="font-medium">
                  {new Date(post.publishedAt).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })}
                </time>
              </div>

              <div className="flex items-center gap-2">
                <SafeIcon icon={FiClock} className="w-4 h-4 text-emerald-500" />
                <span className="font-medium">{post.readTime} read</span>
              </div>

              <div className="flex items-center gap-2">
                <SafeIcon icon={FiUser} className="w-4 h-4 text-emerald-500" />
                <span className="font-medium">{post.author}</span>
              </div>

              <div className="flex items-center gap-2">
                <SafeIcon icon={FiEye} className="w-4 h-4 text-emerald-500" />
                <span className="font-medium">{post.views || '1.2K'} views</span>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Featured Image */}
      <section className="mb-12 lg:mb-16">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="relative overflow-hidden rounded-2xl shadow-2xl"
          >
            <img
              src={post.image}
              alt={post.title}
              className="w-full h-64 sm:h-80 lg:h-96 object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/20 via-transparent to-transparent" />
            <div className="absolute bottom-6 left-6">
              <div className="bg-white/95 backdrop-blur-sm rounded-xl px-4 py-2 shadow-lg">
                <div className="flex items-center gap-2 text-gray-900">
                  <span className="text-lg">📖</span>
                  <span className="font-semibold text-sm">{post.readTime}</span>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Article Content */}
      <article className="pb-20">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
          >
            {/* Content */}
            <div className="prose prose-lg prose-gray max-w-none">
              <RichTextRenderer
                content={post.content}
                className="prose-headings:text-gray-900 prose-headings:font-bold prose-p:text-gray-700 prose-p:leading-relaxed prose-strong:text-gray-900 prose-a:text-emerald-600 prose-a:no-underline hover:prose-a:underline prose-blockquote:border-emerald-200 prose-blockquote:text-gray-700"
              />
            </div>

            {/* Tags */}
            {post.tags && post.tags.length > 0 && (
              <div className="mt-12 pt-8 border-t border-gray-200">
                <div className="flex items-center gap-3 mb-6">
                  <SafeIcon icon={FiTag} className="w-5 h-5 text-emerald-600" />
                  <h3 className="text-lg font-bold text-gray-900">Related Topics</h3>
                </div>
                <div className="flex flex-wrap gap-3">
                  {post.tags.map((tag, index) => (
                    <span
                      key={index}
                      className="px-4 py-2 bg-gray-100 hover:bg-emerald-50 text-gray-700 hover:text-emerald-700 rounded-full text-sm font-medium transition-colors duration-200 cursor-pointer"
                    >
                      #{tag}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Article Footer */}
            <div className="mt-12 pt-8 border-t border-gray-200">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <span className="text-gray-600 font-medium">Share this article:</span>
                  <button
                    onClick={handleShare}
                    className="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-emerald-100 text-gray-700 hover:text-emerald-700 rounded-full transition-colors duration-200"
                  >
                    <SafeIcon icon={FiShare2} className="w-4 h-4" />
                    <span className="font-medium">Share</span>
                  </button>
                </div>

                <div className="flex items-center gap-6 text-sm text-gray-500">
                  <div className="flex items-center gap-2">
                    <SafeIcon icon={FiHeart} className="w-4 h-4" />
                    <span className="font-medium">{post.likes || 42}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <SafeIcon icon={FiMessageCircle} className="w-4 h-4" />
                    <span className="font-medium">{post.comments || 8}</span>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </article>

      {/* Related Articles */}
      {relatedPosts.length > 0 && (
        <section className="py-16 lg:py-24 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-center mb-12 lg:mb-16"
            >
              <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
                Related Articles
              </h2>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                More insights from our experts on similar topics
              </p>
            </motion.div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
              {relatedPosts.map((relatedPost, index) => (
                <motion.div
                  key={relatedPost.id}
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: index * 0.1 }}
                >
                  <Link to={`/blog/${relatedPost.slug}`} className="group block">
                    <div className="bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl border border-gray-100 hover:border-emerald-200 transition-all duration-300 hover:-translate-y-1">
                      {/* Image */}
                      <div className="relative h-48 overflow-hidden">
                        <img
                          src={relatedPost.image}
                          alt={relatedPost.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        />
                        <div className="absolute top-4 left-4">
                          <span className="px-3 py-1 bg-emerald-500 text-white text-xs font-semibold rounded-full">
                            {relatedPost.category}
                          </span>
                        </div>
                      </div>

                      {/* Content */}
                      <div className="p-6">
                        {/* Meta */}
                        <div className="flex items-center gap-4 text-sm text-gray-500 mb-3">
                          <div className="flex items-center gap-1.5">
                            <SafeIcon icon={FiCalendar} className="w-3.5 h-3.5" />
                            <span>{new Date(relatedPost.publishedAt).toLocaleDateString('en-US', {
                              month: 'short',
                              day: 'numeric'
                            })}</span>
                          </div>
                          <div className="flex items-center gap-1.5">
                            <SafeIcon icon={FiClock} className="w-3.5 h-3.5" />
                            <span>{relatedPost.readTime}</span>
                          </div>
                        </div>

                        {/* Title */}
                        <h3 className="text-lg font-bold text-gray-900 mb-3 leading-tight group-hover:text-emerald-600 transition-colors line-clamp-2">
                          {relatedPost.title}
                        </h3>

                        {/* Excerpt */}
                        <p className="text-gray-600 text-sm leading-relaxed line-clamp-2 mb-4">
                          {relatedPost.excerpt}
                        </p>

                        {/* Read More */}
                        <div className="flex items-center gap-2 text-emerald-600 font-semibold text-sm group-hover:gap-3 transition-all">
                          <span>Read More</span>
                          <SafeIcon icon={FiArrowRight} className="w-4 h-4" />
                        </div>
                      </div>
                    </div>
                  </Link>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section className="py-16 lg:py-24 bg-gradient-to-br from-emerald-600 via-emerald-700 to-teal-700">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h2 className="text-3xl lg:text-4xl font-bold text-white mb-6">
              Ready to Transform Your Health?
            </h2>
            <p className="text-xl text-emerald-100 mb-8 leading-relaxed max-w-2xl mx-auto">
              Experience the power of natural healing with Dr. Saurabh Singh. Book your consultation today and start your journey towards optimal wellness.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
              <Link
                to="/contact"
                className="bg-white text-emerald-700 px-8 py-4 rounded-full font-bold hover:bg-emerald-50 transition-all duration-300 shadow-lg hover:shadow-xl"
              >
                Book Consultation
              </Link>
              <Link
                to="/about"
                className="border-2 border-white text-white px-8 py-4 rounded-full font-bold hover:bg-white hover:text-emerald-700 transition-all duration-300"
              >
                Learn More About Us
              </Link>
            </div>
            <div className="flex flex-wrap justify-center gap-6 text-sm text-emerald-200">
              <span>✓ Free initial consultation</span>
              <span>✓ No commitment required</span>
              <span>✓ Results guaranteed</span>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default BlogDetail;